import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiFetch } from "../../../utils";

export const fetchBookingConsole = createAsyncThunk(
    'booking/fetchConsole',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiFetch("booking/t/console");
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message || "Failed to fetch console");
        }
    }
);

export const regenShopKey = createAsyncThunk(
    'booking/regenShopKey',
    async (id, { rejectWithValue }) => {
        try {
            const response = await apiFetch(`booking/t/shops/${id}/key`, {
                method: "POST",
            });
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message || "Failed to fetch console");
        }
    }
);

export const addShopType = createAsyncThunk(
    'booking/addShopType',
    async ({ shopId, payload }, { rejectWithValue }) => {
        try {
            const response = await apiFetch(`booking/t/shops/${shopId}/types`, {
                method: "POST",
                body: JSON.stringify(payload),
                idempotencyAction: "booking:add-type",
            });
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message || "Failed to fetch console");
        }
    }
);

export const updateShopType = createAsyncThunk(
    'booking/updateShopType',
    async ({ id, patch }, { rejectWithValue }) => {
        try {
            const response = await apiFetch(`booking/t/types/${id}`, {
                method: "PATCH",
                body: JSON.stringify(patch)
            });
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message || "Failed to fetch console");
        }
    }
);

export const deleteShopType = createAsyncThunk(
    'booking/deleteShopType',
    async (id, { rejectWithValue }) => {
        try {
            const response = await apiFetch(`booking/t/types/${id}`, {
                method: "DELETE"
            });
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message || "Failed to fetch console");
        }
    }
);

export const addResource = createAsyncThunk(
    'booking/addResource',
    async ({ shopId, typeId, name }, { rejectWithValue }) => {
        try {
            const response = await apiFetch(`booking/t/shops/${shopId}/resources`, {
                method: "POST",
                body: JSON.stringify({ typeId, name }),
                idempotencyAction: "booking:add-resource",
            });
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message || "Failed to fetch console");
        }
    }
);

export const deleteResource = createAsyncThunk(
    'booking/deleteResource',
    async (id, { rejectWithValue }) => {
        try {
            const response = await apiFetch(`booking/t/resources/${id}`, {
                method: "DELETE"
            });
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message || "Failed to fetch console");
        }
    }
);

export const createBooking = createAsyncThunk(
    'booking/create',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await apiFetch(`booking/t/bookings`, {
                method: "POST",
                body: JSON.stringify(payload),
                idempotencyAction: "booking:create",
            });
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message || "Failed to fetch console");
        }
    }
);

export const updateBookingStatus = createAsyncThunk(
    'booking/updateStatus',
    async ({ id, status }, { rejectWithValue }) => {
        try {
            const response = await apiFetch(`booking/t/bookings/${id}/status`, {
                method: "PATCH",
                body: JSON.stringify({ status })
            });
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message || "Failed to fetch console");
        }
    }
);

export const updateBooking = createAsyncThunk(
    'booking/update',
    async ({ id, patch }, { rejectWithValue }) => {
        try {
            const response = await apiFetch(`booking/t/bookings/${id}`, {
                method: "patch",
                body: JSON.stringify(patch)
            });
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message || "Failed to fetch console");
        }
    }
);

export const updateResource = createAsyncThunk(
    'booking/updateResource',
    async ({ id, patch }, { rejectWithValue }) => {
        try {
            const response = await apiFetch(`booking/t/resources/${id}`, {
                method: "PATCH",
                body: JSON.stringify(patch)
            });
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message || "Failed to fetch console");
        }
    }
);

export const rescheduleBooking = createAsyncThunk(
    'booking/reschedule',
    async ({ id, slot }, { rejectWithValue }) => {
        try {
            const response = await apiFetch(`booking/t/bookings/${id}/reschedule`, {
                method: "PATCH",
                body: JSON.stringify(slot)
            });
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message || "Failed to fetch console");
        }
    }
);

const bookingSlice = createSlice({
    name: 'booking',
    initialState: {
        plans: null,
        shops: [],
        resourceTypes: [],
        resources: [],
        bookings: [],
        loading: false,
        error: null,
        notice: null,
    },
    reducers: {
        clearBookingError: (state) => {
            state.error = null;
        },
        clearBookingNotice: (state) => {
            state.notice = null;
        },
    },
    extraReducers: (builder) => {
        const failed = (msg) => (state, action) => {
            state.error = action.payload || msg;
        };
        builder
            .addCase(fetchBookingConsole.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchBookingConsole.fulfilled, (state, action) => {
                state.loading = false;
                state.plans = action.payload?.plans || null;
                state.shops = action.payload?.shops || [];
                state.resourceTypes = action.payload?.resourceTypes || [];
                state.resources = action.payload?.resources || [];
                state.bookings = action.payload?.bookings || [];
            })
            .addCase(fetchBookingConsole.rejected, failed('Failed to load booking console'))
            .addCase(regenShopKey.fulfilled, (state, action) => {
                const idx = state.shops.findIndex((s) => s.id === action.payload?.id);
                if (idx !== -1) state.shops[idx] = action.payload;
                state.notice = 'New API key issued. Old key stopped working.';
            })
            .addCase(regenShopKey.rejected, failed('Failed to issue key'))
            .addCase(addShopType.fulfilled, (state, action) => {
                if (action.payload?.id) state.resourceTypes.push(action.payload);
                state.error = null;
                state.notice = `${action.payload.name} calendar created.`;
            })
            .addCase(addShopType.rejected, failed('Failed to add resource type'))
            .addCase(updateShopType.fulfilled, (state, action) => {
                const idx = state.resourceTypes.findIndex((t) => t.id === action.payload?.id);
                if (idx !== -1) state.resourceTypes[idx] = action.payload;
                state.error = null;
            })
            .addCase(updateShopType.rejected, failed('Failed to update resource type'))
            .addCase(deleteShopType.fulfilled, (state, action) => {
                state.resourceTypes = state.resourceTypes.filter((t) => t.id !== action.payload);
                state.notice = 'Resource type deleted.';
            })
            .addCase(deleteShopType.rejected, failed('Failed to delete resource type'))
            .addCase(addResource.fulfilled, (state, action) => {
                if (action.payload?.id) state.resources.push(action.payload);
                state.error = null;
                state.notice = `${action.payload.name} added.`;
            })
            .addCase(addResource.rejected, failed('Failed to add resource'))
            .addCase(deleteResource.fulfilled, (state, action) => {
                state.resources = state.resources.filter((r) => r.id !== action.payload);
                state.notice = 'Resource deleted.';
            })
            .addCase(deleteResource.rejected, failed('Failed to delete resource'))
            .addCase(updateResource.fulfilled, (state, action) => {
                const idx = state.resources.findIndex((r) => r.id === action.payload?.id);
                if (idx !== -1) state.resources[idx] = action.payload;
                state.error = null;
                state.notice = `Resource renamed to ${action.payload.name}.`;
            })
            .addCase(updateResource.rejected, failed('Failed to rename resource'))
            .addCase(createBooking.fulfilled, (state, action) => {
                if (action.payload?.id) state.bookings.push(action.payload);
                state.error = null;
                state.notice = `Booking ${action.payload.id} received — pending confirmation.`;
            })
            .addCase(createBooking.rejected, failed('Failed to create booking'))
            .addCase(updateBookingStatus.fulfilled, (state, action) => {
                const idx = state.bookings.findIndex((b) => b.id === action.payload?.id);
                if (idx !== -1) state.bookings[idx] = action.payload;
                state.error = null;
            })
            .addCase(updateBookingStatus.rejected, failed('Failed to update booking'))
            .addCase(updateBooking.fulfilled, (state, action) => {
                const idx = state.bookings.findIndex((b) => b.id === action.payload?.id);
                if (idx !== -1) state.bookings[idx] = action.payload;
                state.error = null;
                state.notice = `Booking ${action.payload.id} updated.`;
            })
            .addCase(updateBooking.rejected, failed('Failed to update booking'))
            .addCase(rescheduleBooking.fulfilled, (state, action) => {
                const idx = state.bookings.findIndex((b) => b.id === action.payload?.id);
                if (idx !== -1) state.bookings[idx] = action.payload;
                state.error = null;
                state.notice = `Booking ${action.payload.id} moved.`;
            })
            .addCase(rescheduleBooking.rejected, failed('Failed to reschedule'));
    },
});

export const { clearBookingError, clearBookingNotice } = bookingSlice.actions;
export default bookingSlice.reducer;
